import React, { useState, useRef, useEffect } from "react";
import { Col, Row, Card, Form, Button, Alert, Modal, Collapse } from '@themesberg/react-bootstrap';
import api from '../../utils/api';
import { uploadToR2, generateUUID } from '../../utils/r2Upload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImages, faTrash, faCheckCircle, faPhone, faFire, faCommentDots,
  faUserSecret, faStar, faCrown, faShoppingBag, faWineGlass,
  faComments, faSun, faMusic, faGlassMartiniAlt, faChevronDown, faChevronUp
} from '@fortawesome/free-solid-svg-icons';

const HOBBIES_LIST = [
  { id: "bulusmaya_acik", text: "Randevucu", icon: faCheckCircle },
  { id: "telefon_arkadasligi", text: "Sanal Aşk", icon: faPhone },
  { id: "tensel_yakinlik", text: "Tensel yakınlık", icon: faFire },
  { id: "flort_etmek", text: "Flört etmek", icon: faCommentDots },
  { id: "gizemli_bulusmalar", text: "Gizli aşk", icon: faUserSecret },
  { id: "bastancikarici_stil", text: "Baştan çıkarıcı", icon: faStar },
  { id: "luks_yasam", text: "Lüks yaşam", icon: faCrown },
  { id: "stil_moda", text: "Stil & moda", icon: faShoppingBag },
  { id: "romantik_aksamlar", text: "Romantik", icon: faWineGlass },
  { id: "uzun_sohbetler", text: "Uzun sohbetler", icon: faComments },
  { id: "gun_batimi", text: "Gün batımı", icon: faSun },
  { id: "gece_hayati", text: "Gece hayatı", icon: faMusic },
  { id: "lounge_bar", text: "Lounge & bar keşfi", icon: faGlassMartiniAlt },
  { id: "sulu_seftali", text: "Sulu Şeftali", icon: faFire },
];

// Form'un başlangıç durumu
const initialFormData = {
  firstName: "",
  lastName: "",
  gender: "Kadın",
  age: "",
  bio: "",
  hashtag: "",
  link: "",
  hobbies: [],
  profileImage: null,
  galleryImages: [],
  listSelection: "both", // "both", "nearby", "featured"
  isGoldProfile: false,
  goldContentCost: "",
  voiceRecord: null
};

export default () => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false); // Form gönderildi mi?
  const [archiveImageFiles, setArchiveImageFiles] = useState([]);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showOptionalSettings, setShowOptionalSettings] = useState(false);
  const profileImageInputRef = useRef(null);
  const galleryImagesInputRef = useRef(null);

  // Sayfa yüklendiğinde formu sıfırla (F5 için)
  useEffect(() => {
    // Sayfa yüklendiğinde formu sıfırla
    resetForm();
  }, []);

  // Formu sıfırlama fonksiyonu
  const resetForm = () => {
    setFormData(initialFormData);
    setError(null);
    setSuccess(false);
    setIsSubmitted(false);
    setLoading(false);
    // File input'ları da sıfırla
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = '';
    }
    if (galleryImagesInputRef.current) {
      galleryImagesInputRef.current.value = '';
    }
    const voiceInput = document.getElementById("voice-input");
    if(voiceInput) voiceInput.value = '';
    setArchiveImageFiles([]);
    setShowArchiveModal(false);
    setShowOptionalSettings(false);
  };

  // Yaş validasyonu kontrolü
  const isValidAge = (ageValue) => {
    if (!ageValue || ageValue === '') return false;
    const age = parseInt(ageValue);
    if (isNaN(age)) return false;
    if (age < 18 || age > 99) return false;
    if (ageValue.toString().startsWith('0')) return false; // 0 ile başlayamaz
    if (ageValue.toString().length > 2) return false; // Maksimum 2 hane
    return true;
  };

  // Form validasyonu
  const isFormValid = () => {
    return (
      formData.firstName.trim() !== '' &&
      isValidAge(formData.age) &&
      formData.profileImage !== null
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Yaş validasyonu - sadece geçerli değerleri kabul et
    if (name === 'age') {
      // Boş değere izin ver
      if (value === '') {
        setFormData(prev => ({ ...prev, [name]: '' }));
        if (error) setError(null);
        return;
      }

      // Sadece rakam girişine izin ver
      if (!/^\d+$/.test(value)) {
        return; // Rakam dışı karakter girilemez
      }

      // 0 ile başlayan değerleri engelle (01, 05 gibi)
      if (value.startsWith('0') && value.length > 1) {
        return;
      }

      // Maksimum 2 hane kontrolü
      if (value.length > 2) {
        return;
      }

      const ageValue = parseInt(value);
      // 18-99 arası kontrolü
      if (ageValue < 18 || ageValue > 99) {
        // Değeri kaydet ama buton disabled kalacak
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError(null);
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError(null);
  };

  const handleHobbyToggle = (hobbyText) => {
    setFormData(prev => {
      const hobbies = prev.hobbies || [];
      if (hobbies.includes(hobbyText)) {
        return { ...prev, hobbies: hobbies.filter(h => h !== hobbyText) };
      } else {
        if (hobbies.length >= 5) {
          setError("Maksimum 5 hobi seçebilirsiniz.");
          window.scrollTo(0, 0); // Scroll to error
          return prev;
        }
        if (error) setError(null);
        return { ...prev, hobbies: [...hobbies, hobbyText] };
      }
    });
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Dosya boyutu kontrolü (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(`Profil fotoğrafı çok büyük. Maksimum 10MB olmalıdır. (Seçilen: ${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        e.target.value = ''; // Input'u temizle
        return;
      }
      setFormData(prev => ({ ...prev, profileImage: file }));
      if (error) setError(null);
    }
  };

  const handleGalleryImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + formData.galleryImages.length > 4) {
      setError("Maksimum 4 galeri fotoğrafı ekleyebilirsiniz.");
      return;
    }

    // Dosya boyutu kontrolü (10MB her biri için)
    const maxSize = 10 * 1024 * 1024;
    const oversizedFiles = files.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      setError(`Bazı fotoğraflar çok büyük. Her fotoğraf maksimum 10MB olmalıdır.`);
      e.target.value = ''; // Input'u temizle
      return;
    }

    setFormData(prev => ({
      ...prev,
      galleryImages: [...prev.galleryImages, ...files].slice(0, 4)
    }));
    if (error) setError(null);
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, i) => i !== index)
    }));
  };

  const handleArchiveMergeChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;
    setArchiveImageFiles(prev => [...prev, ...files]);
    e.target.value = ''; // Input'u temizle
  };

  const removeArchiveImage = (index) => {
    setArchiveImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImage = async (file, path, retries = 3) => {
    // Dosya boyutu kontrolü (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error(`Dosya boyutu çok büyük. Maksimum 10MB olmalıdır. (Mevcut: ${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    }

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const downloadURL = await uploadToR2(file, path);
        return downloadURL;
      } catch (error) {
        console.error(`Upload attempt ${attempt} failed:`, error);

        if (attempt === retries) {
          throw new Error(`Dosya yüklenirken hata oluştu: ${error.message}. Lütfen tekrar deneyin.`);
        }

        // Exponential backoff: 2s, 4s, 8s
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Validasyon
      if (!formData.firstName.trim()) {
        setError("Tam isim zorunludur.");
        setLoading(false);
        return;
      }

      if (!isValidAge(formData.age)) {
        setError("Yaş 18-99 arasında olmalıdır. 0 ile başlayamaz.");
        setLoading(false);
        return;
      }
      const ageValue = parseInt(formData.age);

      if (!formData.profileImage) {
        setError("Profil fotoğrafı zorunludur.");
        setLoading(false);
        return;
      }

      // Profil fotoğrafını yükle
      const profileImageUrl = await uploadImage(
        formData.profileImage,
        `test_users/profile_${Date.now()}_${formData.profileImage.name}`
      );

      // Galeri fotoğraflarını yükle
      const galleryImageUrls = [];
      for (const image of formData.galleryImages) {
        const url = await uploadImage(
          image,
          `test_users/gallery_${Date.now()}_${image.name}`
        );
        galleryImageUrls.push(url);
      }

      // Kullanıcı adını oluştur
      const fullName = formData.lastName.trim()
        ? `${formData.firstName.trim()} ${formData.lastName.trim()}`
        : formData.firstName.trim();

      // Liste seçimine göre boolean değerleri belirle
      const showInFeatured = formData.listSelection === "both" || formData.listSelection === "featured";
      const showInNearby = formData.listSelection === "both" || formData.listSelection === "nearby";

      // 1-20 km arasında random mesafe oluştur (0.1 km hassasiyetle)
      // Örnek: 5.6, 10.5, 10.9 gibi değerler (10.99 gibi olmayacak)
      const randomDistance = Math.round((Math.random() * 19 + 1) * 10) / 10; // 1.0 - 20.0 arası, 0.1 hassasiyetle

      // Fake email oluştur (unique olması için timestamp ekle)
      const fakeEmail = `fake_${Date.now()}_${Math.random().toString(36).substring(7)}@test.com`;
      const fakePassword = `Test123!${Date.now()}`; // Güçlü şifre

      const userData = {
        email: fakeEmail,
        password: fakePassword,
        name: fullName,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim() || null,
        gender: formData.gender,
        age: ageValue,
        profileImageUrl: profileImageUrl,
        galleryImages: galleryImageUrls,
        isApproved: true, // Başlangıçta otomatik doğrulanmış
        isTestUser: true, // Test kullanıcısı işareti
        showInFeatured: showInFeatured, // Öne Çıkanlar listesinde göster
        showInNearby: showInNearby, // Yakınındakiler listesinde göster
        isGoldProfile: formData.isGoldProfile, // Gold Profil seçeneği
        goldContentCost: formData.isGoldProfile ? parseInt(formData.goldContentCost) || 0 : 0, // Gold içerik bedeli
        distance: randomDistance, // 0-20 km arası random mesafe
        bio: formData.bio.trim() || null, // Opsiyonel biyografi
        hashtag: formData.hashtag.trim() || "",
        link: formData.link.trim() || null,
        hobbies: formData.hobbies || [], // Seçilen hobiler
        credits: 0
      };

      const response = await api.post('/users/create-approved', userData);
      const result = response.data;
      
      if (result.success) {
        const newUserId = result.user?.uid || result.uid || result.user?.id;
        console.log('Fake user created successfully:', newUserId);

        if (formData.voiceRecord) {
          try {
            const voiceUrl = await uploadImage(
              formData.voiceRecord,
              `test_users/voice_${Date.now()}_${formData.voiceRecord.name}`
            );
            await api.put(`/users/${newUserId}/admin-update`, {
              voiceUrl: voiceUrl
            });
            console.log('Voice record uploaded and linked');
          } catch (voiceErr) {
            console.error('Error uploading voice record:', voiceErr);
          }
        }

        if (archiveImageFiles.length > 0) {
          try {
            const uploadPromises = archiveImageFiles.map(async (file) => {
              const timestamp = Date.now();
              const fileName = `archive_${timestamp}_${file.name}`;
              const downloadUrl = await uploadToR2(file, `archivePhotos/${newUserId}/${fileName}`);

              await api.post(`/users/${newUserId}/archive-photos`, {
                url: downloadUrl,
                fileName: fileName
              });
            });
            await Promise.all(uploadPromises);
          } catch (archiveErr) {
            console.error('Error uploading archive photos:', archiveErr);
          }
        }
      }

      setSuccess(true);
      setIsSubmitted(true); // Form gönderildi, butonu pasif yap
      // Yönlendirmeyi kaldırdık, kullanıcı "Yeni Hesap Ekle" butonuna basabilir
    } catch (err) {
      console.error("Error adding test user:", err);
      setError("Test kullanıcısı eklenirken hata oluştu: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="py-4">
        <h4>Ekle</h4>
        <p className="text-muted mb-0">Hesaplar / Ekle</p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success">
          Test kullanıcısı başarıyla eklendi! Yönlendiriliyorsunuz...
        </Alert>
      )}

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="alert alert-info mb-4">
            <strong>Uyarı:</strong> Bu özellikler test amaçlıyla kullanılmalıdır.
          </div>

          <Form onSubmit={handleSubmit}>
            <h5 className="mb-4">Üye Bilgileri</h5>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Tam İsim <span className="text-danger">*</span>
                  </Form.Label>
                  <Row>
                    <Col>
                      <Form.Control
                        type="text"
                        name="firstName"
                        placeholder="İsim"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </Col>
                    <Col>
                      <Form.Control
                        type="text"
                        name="lastName"
                        placeholder="Soyisim (Opsiyonel)"
                        value={formData.lastName}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Cinsiyet <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="Kadın">Kadın</option>
                    <option value="Erkek">Erkek</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Yaş <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="age"
                    placeholder="18-99"
                    value={formData.age}
                    onChange={handleChange}
                    required
                  />
                  <Form.Text className={isValidAge(formData.age) ? "text-muted" : "text-danger"}>
                    {formData.age === ''
                      ? "Yaş 18-99 arasında olmalıdır. 0 ile başlayamaz."
                      : isValidAge(formData.age)
                        ? "Yaş geçerli."
                        : "Yaş 18-99 arasında olmalıdır. 0 ile başlayamaz."
                    }
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mb-4 mt-4 d-flex align-items-center gap-3">
              Profil Fotoğrafı
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowArchiveModal(true)}
              >
                <FontAwesomeIcon icon={faImages} className="me-2" />
                Fotoğraf Arşivi ({archiveImageFiles.length})
              </Button>
            </h5>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Control
                    type="file"
                    ref={profileImageInputRef}
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    required
                  />
                  {formData.profileImage && (
                    <div className="mt-2">
                      <img
                        src={URL.createObjectURL(formData.profileImage)}
                        alt="Profil önizleme"
                        style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    # <small className="text-muted">(Opsiyonel)</small>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="hashtag"
                    maxLength={40}
                    placeholder="#tekgecelik"
                    value={formData.hashtag}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Keşfet kartında Popüler yerine görünür. Örnek: #yalnız, #tekgecelik, #yatak odası, #flört, #ciddi aşk, #vur kaç, #evlilik. Boş bırakılırsa yazı çıkmaz.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <div className="mb-3">
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => setShowOptionalSettings((open) => !open)}
                aria-expanded={showOptionalSettings}
              >
                <FontAwesomeIcon
                  icon={showOptionalSettings ? faChevronUp : faChevronDown}
                  className="me-2"
                />
                {showOptionalSettings ? 'Opsiyonel ayarları gizle' : 'Opsiyonel ayarları gör'}
              </Button>
            </div>

            <Collapse in={showOptionalSettings}>
              <div>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    Biyografi <small className="text-muted">(Opsiyonel)</small>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="bio"
                    rows={2}
                    maxLength={150}
                    placeholder="İstanbul ✨ Kahve bağımlısı ☕ Seyahat etmeyi severim 🌍"
                    value={formData.bio}
                    onChange={handleChange}
                    style={{ resize: 'none' }}
                  />
                  <Form.Text className="text-muted">
                    {formData.bio.length}/150 karakter (emoji ve satır kırılım destekli)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    Link <small className="text-muted">(Admin panele özel)</small>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="link"
                    placeholder="Manuel yazı, sayı veya herhangi bir karakter girebilirsiniz..."
                    value={formData.link}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Bu alan sadece admin panelinde görünür. Uygulamada veya cevapsız mesajlarda görünmez.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    Hobiler / Özellikler <small className="text-muted">(Opsiyonel, Maksimum 5)</small>
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {HOBBIES_LIST.map((hobby) => {
                      const isSelected = (formData.hobbies || []).includes(hobby.text);
                      return (
                        <div
                          key={hobby.id}
                          onClick={() => handleHobbyToggle(hobby.text)}
                          style={{
                            cursor: 'pointer',
                            padding: '10px 16px',
                            borderRadius: '24px',
                            border: `2px solid ${isSelected ? '#A855F7' : '#E9D5FF'}`,
                            backgroundColor: isSelected ? '#F3E8FF' : '#FFF',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease',
                            userSelect: 'none'
                          }}
                        >
                          <FontAwesomeIcon icon={hobby.icon} style={{ color: isSelected ? '#A855F7' : '#9CA3AF' }} />
                          <span style={{ 
                            color: isSelected ? '#1A1A2E' : '#4B5563', 
                            fontWeight: isSelected ? 'bold' : 'normal',
                            fontSize: '14px' 
                          }}>
                            {hobby.text}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mb-4 mt-4">Ses Kaydı</h5>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    Ses Kaydı Ekle <small className="text-muted">(Opsiyonel - Sadece MP3, AAC vs. desteklenir)</small>
                  </Form.Label>
                  <Form.Control
                    type="file"
                    id="voice-input"
                    accept="audio/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if(file) {
                        setFormData(prev => ({...prev, voiceRecord: file}));
                      }
                    }}
                  />
                  {formData.voiceRecord && (
                    <div className="mt-2 text-success">
                      <FontAwesomeIcon icon={faMusic} className="me-2" /> Seçilen dosya: {formData.voiceRecord.name}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Eklenen ses kaydı kullanıcının profilinde şehir bilgisinin yazdığı yerde özel bir ses oynatıcı ile gösterilir.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mb-4 mt-4">Profil Türü</h5>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="is-gold-profile"
                    label="Gold Profil Yap (İçerikleri sadece Gold aboneler görebilir)"
                    checked={formData.isGoldProfile}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFormData(prev => ({ ...prev, isGoldProfile: isChecked, goldContentCost: isChecked ? prev.goldContentCost : "" }));
                    }}
                  />
                  <Form.Text className="text-muted">
                    Gold profillerin özel içeriklerini görmek için kullanıcılar jeton bedeli ödemelidir.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            {formData.isGoldProfile && (
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Özel İçerik Bedeli (Jeton) <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="goldContentCost"
                      value={formData.goldContentCost}
                      onChange={handleChange}
                      placeholder="Örn: 250"
                      min="1"
                    />
                    <Form.Text className="text-muted">
                      Kullanıcıların bu profilin özel içeriklerini görmek için ödemeleri gereken jeton sayısı.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}
              </div>
            </Collapse>



            <div className="d-flex justify-content-end gap-2">
              {isSubmitted && (
                <Button
                  variant="success"
                  size="lg"
                  onClick={resetForm}
                >
                  Yeni Hesap Ekle
                </Button>
              )}
              <Button
                variant="primary"
                type="submit"
                size="lg"
                disabled={loading || !isFormValid() || isSubmitted}
              >
                {loading ? "Ekleniyor..." : "Ekle"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Arşiv Fotoğrafları Modal */}
      <Modal show={showArchiveModal} onHide={() => setShowArchiveModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Fotoğraf Arşivi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Seçtiğiniz fotoğraflar, hesap eklendiğinde kullanıcının arşivine yüklenecektir. (Hızlı cevapsız mesajlardaki kaynakla aynıdır).
          </p>

          <div className="mb-4">
            <Form.Group>
              <Form.Label>Yeni Fotoğraf Seç (Birden fazla seçebilirsiniz)</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                onChange={handleArchiveMergeChange}
              />
            </Form.Group>
          </div>

          <div className="d-flex flex-wrap gap-3">
            {archiveImageFiles.length === 0 ? (
              <div className="text-center py-4 w-100">
                <p className="text-muted">Henüz seçilmiş arşiv fotoğrafı yok.</p>
              </div>
            ) : (
              archiveImageFiles.map((file, index) => (
                <div
                  key={index}
                  style={{
                    position: 'relative',
                    width: '120px',
                    height: '120px',
                    border: '2px solid #dee2e6',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Seçilen Galeri ${index}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      padding: '2px 6px',
                      fontSize: '12px'
                    }}
                    onClick={() => removeArchiveImage(index)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
              ))
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowArchiveModal(false)}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
